import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { CodeBlock } from '@/components/docs/CodeBlock';

export default function QuickStartPage() {
  return (
    <article className="max-w-3xl" data-testid="docs-quick-start">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">Quick Start</h1>
      <p className="text-base text-white/50 leading-relaxed mb-8">Add skeleton loading to your app in 5 minutes.</p>

      <h2 className="text-xl font-bold tracking-tight mb-4">1. Wrap your component</h2>
      <p className="text-sm text-white/40 mb-4 leading-relaxed">Add a <code className="text-white/60 bg-white/[0.06] px-1.5 py-0.5 rounded text-xs">data-skeleton-name</code> attribute on the wrapper and wrap with <code className="text-white/60 bg-white/[0.06] px-1.5 py-0.5 rounded text-xs">&lt;Skeleton&gt;</code>:</p>
      <CodeBlock filename="app/blog/page.tsx" code={`import { Skeleton } from '@ghostlayout/react'

function BlogPage() {
  const { data, isLoading } = useFetch('/api/post')

  return (
    <div data-skeleton-name="blog-card">
      <Skeleton name="blog-card" loading={isLoading}>
        {data && <BlogCard data={data} />}
      </Skeleton>
    </div>
  )
}`} />

      <h2 className="text-xl font-bold tracking-tight mb-4 mt-10">2. Run the CLI</h2>
      <p className="text-sm text-white/40 mb-4 leading-relaxed">Start your dev server, then in another terminal:</p>
      <CodeBlock language="bash" code={`npx ghostlayout build`} />
      <p className="text-sm text-white/40 mt-3 leading-relaxed">The CLI auto-detects your dev server on common ports (3000, 5173, 8080...) and generates blueprint files:</p>
      <CodeBlock language="bash" code={`src/blueprints/
  blog-card.blueprints.json    # layout data
  registry.js             # auto-import`} />

      <h2 className="text-xl font-bold tracking-tight mb-4 mt-10">3. Import the registry</h2>
      <p className="text-sm text-white/40 mb-4 leading-relaxed">Add one import to your app entry point:</p>
      <CodeBlock filename="app/layout.tsx" code={`import './blueprints/registry'`} />

      <div className="border border-emerald-500/20 bg-emerald-500/[0.04] rounded-lg p-5 mt-8">
        <p className="text-sm text-emerald-400/80 font-medium mb-1">Done!</p>
        <p className="text-sm text-white/40">Your skeletons are now pixel-perfect and responsive. Re-run the CLI whenever your layout changes.</p>
      </div>

      <h2 className="text-xl font-bold tracking-tight mb-4 mt-12">Your project after setup</h2>
      <CodeBlock language="directory" code={`your-app/
├── src/
│   ├── blueprints/                    # generated by CLI
│   │   ├── blog-card.blueprints.json
│   │   ├── profile.blueprints.json
│   │   └── registry.js
│   ├── app/
│   │   ├── layout.tsx            # import './blueprints/registry'
│   │   └── blog/page.tsx
│   └── components/
│       └── BlogCard.tsx
├── ghostlayout.config.json       # optional
└── package.json`} />

      <div className="mt-10 flex gap-3">
        <Link to="/docs/skeleton" className="inline-flex items-center gap-2 bg-white text-black px-4 py-2 rounded-md text-sm font-semibold hover:bg-gray-200 transition-colors" data-testid="next-skeleton-btn">
          Skeleton API <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </article>
  );
}
